import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Download, Loader2, X } from "lucide-react";

import { HGRecordMedia } from "@/common/types";
import { useImageZoom } from "@/hooks/use-image-zoom";
import { Dialog, DialogPortal, DialogTitle } from "../dialog";

// Enter animation for a newly shown photo: zoom in on open, slide in from the
// direction of travel when navigating.
function enterAnimClass(dir: "open" | "next" | "prev") {
  const base = "animate-in fade-in-0 duration-200";
  if (dir === "next") return `${base} slide-in-from-right-8`;
  if (dir === "prev") return `${base} slide-in-from-left-8`;
  return `${base} zoom-in-95`;
}

export function MediaLightbox({
  images,
  index,
  onIndexChange,
}: {
  images: HGRecordMedia[];
  index: number | null;
  onIndexChange: (index: number | null) => void;
}) {
  const open = index !== null;
  return (
    <Dialog
      // Non-modal so Radix does not wrap the content in react-remove-scroll,
      // which would preventDefault touchmove and swallow swipe/pan gestures.
      modal={false}
      open={open}
      onOpenChange={(o) => {
        if (!o) onIndexChange(null);
      }}
    >
      {open && index !== null && (
        <LightboxInner
          images={images}
          index={index}
          onIndexChange={onIndexChange}
        />
      )}
    </Dialog>
  );
}

function LightboxInner({
  images,
  index,
  onIndexChange,
}: {
  images: HGRecordMedia[];
  index: number;
  onIndexChange: (index: number | null) => void;
}) {
  const media = images[index];
  const count = images.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const {
    scale,
    tx,
    ty,
    isZoomed,
    stateRef,
    zoomBy,
    zoomTo,
    toggle,
    beginPan,
    panMove,
    reset,
  } = useImageZoom(containerRef);
  const [interacting, setInteracting] = useState(false);

  // Track whether the full-size image for the current index has loaded so we
  // can hide the previous photo and show a spinner while it arrives. Reset
  // during render (not an effect) when the image changes to avoid a flash of
  // the stale photo -- state, not a ref, since a ref can't be read or
  // written safely during render.
  const [loaded, setLoaded] = useState(false);
  const [shownId, setShownId] = useState<string | null>(null);
  if (shownId !== media.id) {
    setShownId(media.id);
    if (loaded) setLoaded(false);
  }

  // Live drag offset (photo follows the finger) + backdrop opacity, for the
  // drag-to-dismiss animation. Direction of the last navigation drives the
  // enter animation of the incoming photo -- also state: it feeds directly
  // into what gets rendered below.
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [backdrop, setBackdrop] = useState(0);
  const [enterDir, setEnterDir] = useState<"open" | "next" | "prev">("open");

  const goTo = useCallback(
    (i: number) => onIndexChange(Math.max(0, Math.min(count - 1, i))),
    [count, onIndexChange],
  );
  const goNext = useCallback(() => {
    setEnterDir("next");
    goTo(index + 1);
  }, [goTo, index]);
  const goPrev = useCallback(() => {
    setEnterDir("prev");
    goTo(index - 1);
  }, [goTo, index]);
  const close = useCallback(() => onIndexChange(null), [onIndexChange]);

  // Fade the backdrop in on open.
  useEffect(() => {
    const id = requestAnimationFrame(() => setBackdrop(1));
    return () => cancelAnimationFrame(id);
  }, []);

  // Lock body scroll while open (non-modal Dialog skips Radix's own lock).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Reset zoom whenever the shown image changes.
  useEffect(() => reset(), [index, reset]);

  // Catch already-cached images whose load event may have fired before mount.
  // Genuinely needs an effect: `.complete`/`.naturalWidth` are read off the
  // DOM node itself, not derivable during render.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoaded(true);
    }
  }, [index]);

  // Preload neighbours for snappy navigation.
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      if (i >= 0 && i < count) {
        const img = new Image();
        img.src = images[i].url;
      }
    });
  }, [index, count, images]);

  // Arrow-key navigation (Escape is handled by Radix).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  // Desktop wheel zoom (native, non-passive so we can preventDefault).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomBy]);

  // Touch/mouse gestures via native Pointer Events (Hammer's touch input was
  // being swallowed inside the Radix layer). Two pointers = pinch-zoom; one
  // pointer = pan when zoomed, otherwise a swipe: horizontal to navigate,
  // downward to dismiss. A stationary release is a tap (double-tap zooms the
  // image; a tap on empty backdrop closes).
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({
    mode: "none" as "none" | "pan" | "swipe" | "pinch",
    startX: 0,
    startY: 0,
    startDist: 0,
    startScale: 1,
    moved: false,
  });
  const lastTap = useRef(0);

  const onPointerDown = (e: React.PointerEvent) => {
    try {
      containerRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* pointer may already be gone */
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      gesture.current.mode = "pinch";
      gesture.current.startDist = Math.hypot(
        pts[0].x - pts[1].x,
        pts[0].y - pts[1].y,
      );
      gesture.current.startScale = stateRef.current.scale;
      setInteracting(true);
    } else if (pts.length === 1) {
      const zoomed = stateRef.current.scale > 1.01;
      gesture.current = {
        mode: zoomed ? "pan" : "swipe",
        startX: e.clientX,
        startY: e.clientY,
        startDist: 0,
        startScale: stateRef.current.scale,
        moved: false,
      };
      if (zoomed) beginPan();
      // Interacting disables the transform/backdrop transitions so both follow
      // the finger 1:1; releasing re-enables them to animate to rest.
      setInteracting(true);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (g.mode === "pinch") {
      const pts = [...pointers.current.values()];
      if (pts.length >= 2 && g.startDist > 0) {
        const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const cx = (pts[0].x + pts[1].x) / 2;
        const cy = (pts[0].y + pts[1].y) / 2;
        zoomTo(g.startScale * (d / g.startDist), cx, cy);
      }
      return;
    }
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.moved && Math.hypot(dx, dy) > 8) g.moved = true;
    if (g.mode === "pan") {
      panMove(dx, dy);
    } else if (g.mode === "swipe") {
      // Follow the finger vertically for the dismiss gesture (horizontal
      // switching is conveyed by the incoming photo's slide-in animation).
      // Dragging down also fades the backdrop.
      setDrag({ x: 0, y: dy });
      setBackdrop(
        dy > 0 ? Math.max(0.3, 1 - dy / (window.innerHeight * 0.7)) : 1,
      );
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    try {
      containerRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer may already be gone */
    }
    // Last tracked position (updated on move) is a reliable release point.
    const rec = pointers.current.get(e.pointerId);
    const endX = rec ? rec.x : e.clientX;
    const endY = rec ? rec.y : e.clientY;
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g.mode === "pinch") {
      // A finger lifted mid-pinch: rebase to a pan/swipe for the remaining one.
      const rest = [...pointers.current.values()];
      if (rest.length === 1) {
        const zoomed = stateRef.current.scale > 1.01;
        gesture.current = {
          mode: zoomed ? "pan" : "swipe",
          startX: rest[0].x,
          startY: rest[0].y,
          startDist: 0,
          startScale: stateRef.current.scale,
          moved: true,
        };
        if (zoomed) beginPan();
      } else {
        gesture.current.mode = "none";
        setInteracting(false);
      }
      return;
    }
    if (pointers.current.size > 0) return;
    setInteracting(false);
    // Capture the mode before resetting: g aliases gesture.current, so
    // resetting first would make the pan check below always fail and let a
    // zoomed pan fall through to swipe navigation. A *moved* pan is done here;
    // a stationary pointer while zoomed still falls through as a tap so
    // double-tap can zoom back out.
    const mode = g.mode;
    gesture.current.mode = "none";
    if (mode === "pan" && g.moved) return;

    const dx = endX - g.startX;
    const dy = endY - g.startY;
    if (!g.moved) {
      const r = imgRef.current?.getBoundingClientRect();
      const onImage =
        !!r &&
        endX >= r.left &&
        endX <= r.right &&
        endY >= r.top &&
        endY <= r.bottom;
      if (onImage) {
        // Double-tap on the image toggles zoom; a single tap does nothing.
        const now = Date.now();
        if (now - lastTap.current < 300) {
          toggle(endX, endY);
          lastTap.current = 0;
        } else {
          lastTap.current = now;
        }
      } else if (stateRef.current.scale <= 1.01) {
        // Tap outside the image (backdrop) closes.
        close();
      }
      return;
    }
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (dy > 110 && absY > absX) {
      // Drag-to-dismiss: fling the photo off the bottom, fade out, then close.
      setDrag({ x: 0, y: window.innerHeight });
      setBackdrop(0);
      window.setTimeout(close, 220);
    } else if (absX > 50 && absX > absY) {
      // Switch: the incoming photo animates in from the swipe direction.
      setDrag({ x: 0, y: 0 });
      setBackdrop(1);
      if (dx < 0) goNext();
      else goPrev();
    } else {
      // Not far enough: snap the photo back and restore the backdrop.
      setDrag({ x: 0, y: 0 });
      setBackdrop(1);
    }
  };

  const onPointerCancel = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    gesture.current.mode = "none";
    setInteracting(false);
    setDrag({ x: 0, y: 0 });
    setBackdrop(1);
  };

  const label = media.caption || media.original_filename;

  return (
    <DialogPortal>
      <DialogPrimitive.Content
        aria-describedby={undefined}
        className="fixed inset-0 z-50 border-0 bg-transparent p-0 shadow-none outline-none focus:outline-none"
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>

        {/* Backdrop as its own layer so drag-to-dismiss can fade it. */}
        <div
          className="absolute inset-0 bg-black"
          style={{
            opacity: backdrop,
            transition: interacting ? "none" : "opacity 0.2s ease-out",
          }}
        />

        {/* Image + gesture surface. Tapping empty space closes when not zoomed. */}
        <div
          ref={containerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          className="absolute inset-0 flex touch-none items-center justify-center overflow-hidden select-none"
        >
          {/* Keyed per photo so the enter animation replays and no stale bitmap
              lingers from the reused element. */}
          <div
            key={media.id}
            className={`flex h-full w-full items-center justify-center ${enterAnimClass(
              enterDir,
            )}`}
          >
            <img
              ref={imgRef}
              src={media.url}
              alt={label}
              draggable={false}
              onLoad={() => setLoaded(true)}
              style={{
                transform: `translate3d(${tx + drag.x}px, ${ty + drag.y}px, 0) scale(${scale})`,
                transition: interacting ? "none" : "transform 0.2s ease-out",
                cursor: isZoomed ? "grab" : "zoom-in",
              }}
              className={`max-h-full max-w-full object-contain transition-opacity duration-200 ${
                loaded ? "opacity-100" : "opacity-0"
              }`}
            />
          </div>
          {!loaded && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-9 w-9 animate-spin text-white/70" />
            </div>
          )}
        </div>

        {/* Top bar: counter + download + close */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-3">
          <span className="pointer-events-auto rounded-full bg-black/40 px-3 py-1 font-mono text-[12px] text-white/90">
            {count > 1 ? `${index + 1} / ${count}` : ""}
          </span>
          <div className="pointer-events-auto flex items-center gap-2">
            <a
              href={media.download_url}
              download={media.original_filename}
              onClick={(e) => e.stopPropagation()}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60"
              aria-label="Download"
            >
              <Download className="h-[18px] w-[18px]" />
            </a>
            <button
              type="button"
              onClick={close}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60"
              aria-label="Close"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        {/* Prev / next arrows (reliable tap target on every screen size) */}
        {count > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              disabled={index === 0}
              className="absolute top-1/2 left-3 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60 disabled:pointer-events-none disabled:opacity-0"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={index === count - 1}
              className="absolute top-1/2 right-3 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white/90 transition-colors hover:bg-black/60 disabled:pointer-events-none disabled:opacity-0"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Caption */}
        {media.caption && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center p-4">
            <span className="pointer-events-auto max-w-[90%] rounded-lg bg-black/40 px-3 py-1.5 text-center text-[13px] text-white/90">
              {media.caption}
            </span>
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}
